/**
 * Authenticated fetch helpers for client-side API requests
 * 
 * These helpers automatically attach the Supabase access_token as a Bearer token
 * to all requests, enabling API routes to authenticate via Authorization header
 * in addition to cookies.
 * 
 * Usage:
 *   import { authFetch, authJson } from '@/lib/authFetch';
 *   
 *   // Basic fetch with auth
 *   const response = await authFetch('/api/notifications');
 *   
 *   // Fetch and parse JSON with type safety
 *   const data = await authJson<NotificationRecord[]>('/api/notifications?limit=50');
 * 
 * Requirements:
 * - Client-side only (uses browser Supabase client)
 * - Uses singleton Supabase client (no new instances)
 * - Throws typed error if no session (caller handles redirect)
 * - Vercel-safe (same-origin relative URLs)
 */

'use client';

import { getSupabaseBrowserClient } from '@/lib/supabaseClient';

/**
 * Error thrown when no authenticated session is available
 */
export class NoSessionError extends Error {
  constructor(message = 'No authenticated session available') {
    super(message);
    this.name = 'NoSessionError';
  }
}

/**
 * Authenticated fetch that automatically attaches Authorization header
 * 
 * @param input - Request URL or Request object
 * @param init - Optional fetch init options (headers are merged, not replaced)
 * @returns Promise<Response>
 * @throws NoSessionError if no authenticated session is available
 */
export async function authFetch(
  input: RequestInfo | URL,
  init?: RequestInit
): Promise<Response> {
  // Get singleton Supabase client
  const supabase = getSupabaseBrowserClient();
  
  // Get current session (uses deduplicated getSession() wrapper)
  const { data: { session }, error } = await supabase.auth.getSession();
  
  if (error || !session?.access_token) {
    throw new NoSessionError(
      error?.message || 'No authenticated session available'
    );
  }
  
  // Merge Authorization header with existing headers
  const headers = new Headers(init?.headers);
  headers.set('Authorization', `Bearer ${session.access_token}`);
  
  // TEMPORARY: Dev-only logging to confirm Authorization header attachment
  if (process.env.NODE_ENV !== 'production') {
    const url = typeof input === 'string' ? input : input instanceof URL ? input.toString() : input.url;
    console.log('[DEV] authFetch', {
      url,
      hasAuthorizationHeader: true, // Always true since we just set it
    });
  }
  
  // Build fetch options with merged headers
  const fetchInit: RequestInit = {
    ...init,
    headers,
    // Always include credentials for cookie fallback
    credentials: 'include',
  };
  
  return fetch(input, fetchInit);
}

/**
 * Authenticated fetch that parses JSON response with type safety
 * 
 * @param input - Request URL or Request object
 * @param init - Optional fetch init options
 * @returns Promise<T> - Parsed JSON response typed as T
 * @throws NoSessionError if no authenticated session is available
 * @throws Error if response is not ok or JSON parsing fails
 */
export async function authJson<T = unknown>(
  input: RequestInfo | URL,
  init?: RequestInit
): Promise<T> {
  const response = await authFetch(input, {
    ...init,
    headers: {
      ...init?.headers,
      'Content-Type': 'application/json',
    },
  });
  
  if (!response.ok) {
    // Try to extract error message from response
    let errorMessage = `Request failed with status ${response.status}`;
    try {
      const errorData = await response.json() as { error?: string; message?: string };
      errorMessage = errorData.error || errorData.message || errorMessage;
    } catch {
      // If JSON parsing fails, use status text
      errorMessage = response.statusText || errorMessage;
    }
    
    throw new Error(errorMessage);
  }
  
  // Parse JSON with type safety
  const json = await response.json() as unknown;
  
  // Return typed result (caller is responsible for type safety)
  return json as T;
}

